import { API_URL, API_KEY } from "@/lib/api-config"

export const MAX_FILE_SIZE_BYTES = 300 * 1024 * 1024

export type DocumentStatus =
  | "pending-upload"
  | "uploading"
  | "uploaded"
  | "splitting"
  | "completed"
  | "failed"

export interface DocumentInfo {
  documentId: string
  fileName: string
  totalPages: number
  fileSizeBytes: number
  status: DocumentStatus
}

export interface SplitOptions {
  mode: "by-page-count" | "by-file-size" | "custom-ranges"
  pagesPerChunk?: number
  overlapPages?: number
  maxFileSizeMB?: number
  customRanges?: string
}

export interface ChunkInfo {
  chunkId: string
  chunkNumber: number
  startPage: number
  endPage: number
  fileSizeBytes: number
  fileName?: string
  status: "pending" | "processing" | "ready"
}

export interface StorageQuota {
  storage: {
    usedBytes: number
    limitBytes: number
    usagePercentage: number
    remainingBytes: number
  }
}

export class ApiError extends Error {
  retryAfter?: number
  constructor(message: string, retryAfter?: number) {
    super(message)
    this.retryAfter = retryAfter
  }
}

export async function getPdfPageCount(file: File): Promise<number> {
  const pdfjsLib = await import("pdfjs-dist")

  pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
    "pdfjs-dist/build/pdf.worker.min.mjs",
    import.meta.url,
  ).toString()

  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = async (e) => {
      let pdf = null
      try {
        const typedArray = new Uint8Array(e.target?.result as ArrayBuffer)
        pdf = await pdfjsLib.getDocument({ data: typedArray }).promise
        const numPages = pdf.numPages
        resolve(numPages)
      } catch (error) {
        reject(error)
      } finally {
        if (pdf) {
          pdf.destroy()
        }
      }
    }
    reader.onerror = reject
    reader.readAsArrayBuffer(file)
  })
}

export function calculateExpectedChunkCount(
  totalPages: number,
  pagesPerChunk: number,
  overlapPages: number,
): number {
  if (totalPages <= 0 || pagesPerChunk <= 0) return 0
  if (totalPages <= pagesPerChunk) return 1

  const effectiveStep = pagesPerChunk - overlapPages
  if (effectiveStep <= 0) return Math.ceil(totalPages / pagesPerChunk)

  return Math.ceil((totalPages - overlapPages) / effectiveStep)
}

export function calculateExpectedChunks(
  totalPages: number,
  pagesPerChunk: number,
  overlapPages: number,
): { chunkNumber: number; startPage: number; endPage: number }[] {
  const chunks: { chunkNumber: number; startPage: number; endPage: number }[] = []

  let chunkNumber = 1
  let currentPage = 1

  while (currentPage <= totalPages) {
    const startPage = currentPage
    const endPage = Math.min(currentPage + pagesPerChunk - 1, totalPages)

    chunks.push({ chunkNumber, startPage, endPage })

    if (endPage >= totalPages) break

    currentPage = endPage - overlapPages + 1
    chunkNumber++
  }

  return chunks
}

export async function requestUploadUrl(
  fileName: string,
  fileSize: number,
  metadata?: { totalPages?: number; title?: string; author?: string },
): Promise<{ documentId: string; uploadUrl: string }> {
  const response = await fetch(`${API_URL}/api/v1/pdf/upload`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-API-Key": API_KEY,
    },
    body: JSON.stringify({
      fileName,
      fileSizeBytes: fileSize,
      contentType: "application/pdf",
      clientMetadata: metadata,
    }),
  })

  if (!response.ok) {
    const retryAfter = response.headers.get("Retry-After")
    const error = await response
      .json()
      .catch(() => ({ detail: "Failed to request upload URL" }))
    const message =
      error.detail || error.message || "Failed to request upload URL"

    if (response.status === 429) {
      throw new ApiError(message, retryAfter ? parseInt(retryAfter) : 60)
    }
    throw new ApiError(message)
  }

  return response.json()
}

export async function uploadToMinIO(uploadUrl: string, file: File): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": "application/pdf",
    },
  })

  if (!response.ok) {
    throw new Error("Failed to upload file to storage")
  }
}

export async function confirmUpload(
  documentId: string,
): Promise<{ totalPages: number }> {
  const response = await fetch(
    `${API_URL}/api/v1/pdf/${documentId}/confirm-upload`,
    {
      method: "POST",
      headers: {
        "X-API-Key": API_KEY,
        "Content-Type": "application/json",
      },
    },
  )

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: "Failed to confirm upload" }))
    throw new Error(error.message || "Failed to confirm upload")
  }

  return response.json()
}

export async function splitPdf(
  documentId: string,
  options: SplitOptions,
): Promise<{ chunks: ChunkInfo[] }> {
  const apiOptions = {
    chunkPages: options.pagesPerChunk || 50,
    overlapPages: options.overlapPages || 2,
  }

  const response = await fetch(`${API_URL}/api/v1/pdf/${documentId}/split`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-API-Key": API_KEY,
    },
    body: JSON.stringify(apiOptions),
  })

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ detail: "Failed to split PDF" }))
    const message = error.detail || error.message || "Failed to split PDF"

    if (response.status === 500 && message.includes("queue")) {
      throw new ApiError(message, 120)
    }
    throw new ApiError(message)
  }

  return response.json()
}

export async function getChunkDownloadUrl(
  documentId: string,
  chunkId: string,
): Promise<{ downloadUrl: string }> {
  const response = await fetch(
    `${API_URL}/api/v1/pdf/${documentId}/chunks/${chunkId}/download`,
    {
      headers: {
        "X-API-Key": API_KEY,
      },
    },
  )

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: "Failed to get download URL" }))
    throw new Error(error.message || "Failed to get download URL")
  }

  return response.json()
}

export async function getStorageQuota(): Promise<StorageQuota> {
  const response = await fetch(`${API_URL}/api/v1/pdf/quota`, {
    headers: {
      "X-API-Key": API_KEY,
    },
  })

  if (!response.ok) {
    throw new Error("Failed to get quota")
  }

  return response.json()
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes"
  const k = 1024
  const sizes = ["Bytes", "KB", "MB", "GB"]
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i]
}
